// =============================================================================
// io/sync/importFile.ts — pick the right parser for a file, and say when none fits.
// -----------------------------------------------------------------------------
// Dispatch only. Every parser here is already ported and parity-tested against
// the old client; this decides which one a given file is for.
//
// ── 🚨 AN UNRECOGNISED FILE MUST SAY SO ──
//
// The one behaviour worth more than the dispatch itself. A file whose headers
// match nothing has to produce a NAMED failure listing what was found, because
// the alternative — running a parser anyway and importing whatever it makes of
// the columns — writes garbage trades that look real. Beta's own parsers return
// `skipped` counts for the same reason.
//
// ⚠ ORDER IS PART OF THE DISPATCH, not an implementation detail. Rithmic is
// detected on the WHOLE TEXT before any row parsing (its header is not line 1),
// and Tradovate's paired report is tested before Sierra because a Performance
// Report satisfies most of the Sierra signature. Each is commented at its site.
//
// ── ALL SEVEN FORMATS ARE NOW HERE ──
//
// TopstepX, Tradesea and MotiveWave were the three the old client read and this
// one did not; a file from any of them used to be reported as unrecognised BY
// NAME, which was the honest failure but still a failure. They now sit in the
// old client's own relative order — ahead of the ATAS and Tradovate tests — so
// that a file matching two signatures resolves the same way in both clients.
//
// ⚠ None of the three actually collides with an ATAS or Tradovate signature
// today (checked column by column). The order is kept anyway: it costs nothing,
// and the alternative is a difference between the clients that is invisible
// until the day some export adds a column.
// =============================================================================

import {
  atasFillsIntegrity, classifySheet, parseAtasFillsFromRows, parseAtasXlsxRows,
  adaptXlsxFillRow,
} from '../parsers/atas';
import { parseSierraFills } from '../parsers/sierra';
import {
  extractRithmicCompletedOrders, isTradovatePaired, looksLikeRithmic,
  pairFillsIntoTrades, parseRithmic, parseTradovatePaired,
} from '../parsers/broker';
import {
  looksLikeMotiveWave, looksLikeTopstepX, looksLikeTradesea,
  parseMotiveWave, parseTopstepX, parseTradesea,
} from '../parsers/platforms';
import { parseTtFills } from '../parsers/tt';
import { parseJigsawRows } from '../parsers/jigsaw';
import { parseDelimited } from '../parsers/delimited';
import { parseXlsxSheet, parseXlsxSharedStrings, xlsxRowsToObjects, type SheetRow } from '../parsers/xlsx';
import { selfCheckFills, type SelfCheckResult } from '../parsers/selfCheck';
import type { CarriedInClosed, ImportedTrade, OpenPosition } from '../parsers/sierra';
import type { PriorPosition } from '../parsers/carryIn';
import type { Row } from '../parsers/columns';
import { findContract, type ContractSpec } from '../../domain/trade';

export type ImportFormat =
  | 'sierra-fills' | 'atas-fills' | 'atas-xlsx'
  | 'tradovate-paired' | 'tradovate-fills' | 'rithmic'
  | 'topstepx' | 'tradesea' | 'motivewave' | 'tt-xlsx-fills' | 'jigsaw-xlsx';

export interface ParseFileResult {
  format: ImportFormat | null;
  trades: ImportedTrade[];
  /** Rows the parser could not use. Counted, never silent. */
  skipped: number;
  /**
   * Contracts that CLOSED a position opened before the file starts — an
   * overnight hold, or a part-day export.
   *
   * ⚠ OPTIONAL because only the fills parsers can produce it; a paired export is
   * already grouped into round trips by the platform. It is surfaced in the
   * import preview because it is the one number that explains a short file: the
   * position was real, we simply have no entry price to value it with.
   */
  carriedIn?: number;
  /**
   * Fills where the exporter's own position disagreed with the parser's.
   *
   * 🚨 NOT COSMETIC. Each one means the fills were paired wrongly, which changes
   * sides, prices and P&L while producing trades that look entirely plausible.
   * It is surfaced in the preview because import time is the only moment it can
   * be acted on cheaply.
   */
  positionMismatches?: number;
  /**
   * Fills Sierra re-pulled from the broker after the fact, and the total they
   * are a share of.
   *
   * 🚨 SURFACED BECAUSE THE CHECKS GO QUIET, NOT BECAUSE THEY PASS. Those rows
   * carry no `PositionQuantity` and stamp `OpenClose = Open` on everything, so
   * both the carried-in guard and the position cross-check are blind on them.
   * A preview that said nothing would be reporting "no problems found" when what
   * happened is "most of this file could not be checked". See #35.
   */
  unverifiedFills?: number;
  totalFills?: number;
  /**
   * Fills the file calls `Open` that the parser spent CLOSING a position.
   *
   * 🚨 Each one is the exporter and the parser disagreeing about what a fill
   * did — the shape D145 cost real money on, caught at the failing row.
   */
  openCloseConflicts?: number;
  /**
   * Positions carried in from an EARLIER import that this file closed (#12).
   *
   * 🚨 Each one is a trade that would otherwise have been a phantom on the
   * opposite side. Surfaced in the preview, and its fingerprints are what the
   * commit marks as consumed.
   */
  carriedInClosed?: CarriedInClosed[];
  /**
   * Set when the parser fell back to a lower-fidelity sheet, or used one it
   * knows is incomplete.
   *
   * 🚨 The user MUST be told. A silent fallback produces a different total from
   * the one they are looking at in their platform, and the first thing they will
   * do is distrust the app rather than the file.
   */
  degraded?: DegradedImport;
  /**
   * Positions the file leaves unfinished.
   *
   * 🚨 EVERY FILLS WALKER HAS ALWAYS COMPUTED THIS, with a comment calling it
   * "surfaced, not dropped" and a fingerprint built so a re-import would not
   * duplicate it — and until now this dispatcher THREW IT AWAY, so nothing in
   * the app had ever seen one. The same shape as D149's gate that was written
   * and never called.
   *
   * It matters most beside the reconciliation below: an unfinished position is
   * the honest explanation for a file whose buys and sells do not balance, and
   * without it on screen the user cannot tell that apart from lost fills.
   */
  openPositions?: OpenPosition[];
  /**
   * Does the output reconcile with the file? See `parsers/selfCheck.ts`.
   *
   * ⚠ Present only for per-fill sources. A paired export has no fills to
   * reconcile against — its rows already ARE the round trips.
   */
  selfCheck?: SelfCheckResult;
  /** Set when nothing matched. Names the headers so the user can tell us. */
  unrecognised: string | null;
}

/**
 * Wrap a per-fill parse with the reconciliation checks.
 *
 * ⚠ The RAW rows are passed, before de-duplication and before skipping. A check
 * run on the rows that survived cannot see the ones that did not, and that is
 * precisely the D151 failure — fills discarded as duplicates before the walk.
 */
function withSelfCheck(
  format: ImportFormat,
  r: { trades: ImportedTrade[]; openPositions: OpenPosition[]; skipped: number },
  rawRows: readonly Row[],
  catalogue: readonly ContractSpec[],
  carriedIn: number,
  extra: Partial<ParseFileResult> = {},
): ParseFileResult {
  const dppFor = (root: string): number => findContract(root, catalogue)?.dollarsPerPoint ?? 0;
  return {
    format,
    trades: r.trades,
    skipped: r.skipped,
    openPositions: r.openPositions,
    selfCheck: selfCheckFills(rawRows, r.trades, r.openPositions, carriedIn, dppFor),
    unrecognised: null,
    ...extra,
  };
}

export interface DegradedImport {
  reason: 'atas-fills-incomplete' | 'atas-fills-incomplete-no-fallback';
  /** Fills carrying a real quantity but no price — the proof of missing data. */
  droppedNoPrice: number;
  /** Which instruments were affected, for the message. */
  symbols: string[];
  /** Trades the discarded path had produced, when we swapped sheets. */
  discardedTrades: number;
}

/** Header sets that identify a Sierra Chart fills export. */
function looksLikeSierra(headers: readonly string[]): boolean {
  const h = headers.map((x) => x.trim().toLowerCase());
  const has = (...names: string[]) => names.some((n) => h.includes(n));
  // ⚠ `FilledQuantity` / `FillPrice` are the Sierra spellings (one word). The
  // parser's own note says a `Quantity` column may also exist holding the ORDER
  // quantity, so presence of Quantity alone is not a Sierra signal.
  return has('symbol', 'instrument')
    && has('filledquantity', 'filled quantity', 'fillquantity', 'fill quantity')
    && has('fillprice', 'fill price', 'averagefillprice', 'price');
}

const delim = (text: string): string => {
  const first = text.split(/\r?\n/, 1)[0] ?? '';
  // Count, don't guess: a comma inside a quoted field would beat a naive test.
  const tabs = (first.match(/\t/g) ?? []).length;
  const commas = (first.match(/,/g) ?? []).length;
  const semis = (first.match(/;/g) ?? []).length;
  if (tabs >= commas && tabs >= semis && tabs > 0) return '\t';
  if (semis > commas && semis > 0) return ';';
  return ',';
};

/**
 * Tradovate Order History (fills): a B/S column plus a filled-quantity and an
 * average-fill-price column.
 *
 * ⚠ All THREE required. `Symbol` + `Price` alone would swallow half the CSVs in
 * the world into a walker that pairs them into invented round trips.
 */
function looksLikeTradovateFills(headers: readonly string[]): boolean {
  const h = headers.map((x) => x.trim().toLowerCase());
  const has = (...names: string[]) => names.some((n) => h.includes(n));
  return has('b/s', 'buyorsell', 'buy/sell')
    && has('filled qty', 'filledqty', 'fillqty')
    && has('avg fill price', 'avgfillprice', 'fillprice', 'fill price');
}

/** A delimited text export — CSV / TSV / TXT. */
export function parseDelimitedImport(
  text: string,
  catalogue: readonly ContractSpec[],
  today: string,
  /**
   * Positions earlier imports left open, so a fill that closes one produces a
   * real cross-session trade instead of a phantom on the opposite side (#12).
   *
   * ⚠ DEFAULTS TO NONE, and with none every parser behaves exactly as it did
   * before. That is what lets the store be absent, empty, or unreachable
   * without changing a single import.
   */
  prior: readonly PriorPosition[] = [],
): ParseFileResult {
  // ⚠ RITHMIC IS DETECTED ON THE WHOLE TEXT, BEFORE ANY ROW PARSING. Its export
  // is a multi-section file whose real header is not line 1, so reading it as a
  // normal CSV yields one column called "Working Orders" and no usable rows —
  // it would fall through to "unrecognised" with a nonsense column list.
  if (looksLikeRithmic(text)) {
    const r = parseRithmic(text, catalogue, today);
    if (!r.trades.length && !r.skipped) {
      return {
        format: null, trades: [], skipped: 0,
        unrecognised:
          'This looks like a Rithmic R Trader export, but its "Completed Orders" '
          + 'section has no filled rows to read.',
      };
    }
    // ⚠ The reconciliation needs the rows the parser actually walked, which for
    // Rithmic are the ones inside the "Completed Orders" section rather than the
    // whole file. Re-extracted here rather than passed out of the parser, so the
    // check keeps its own reading of the file — see selfCheck.ts.
    return withSelfCheck(
      'rithmic', r, extractRithmicCompletedOrders(text, delim(text)), catalogue, 0,
    );
  }

  const rows = parseDelimited(text, delim(text));
  if (!rows.length) {
    return { format: null, trades: [], skipped: 0, unrecognised: 'The file has no data rows.' };
  }

  const headers = Object.keys(rows[0] as Record<string, string>);

  // ── The three platform formats, in the old client's own order ─────────────
  // ⚠ Ahead of the ATAS and Tradovate tests. See the header for why the order is
  // kept even though nothing collides today.

  if (looksLikeTopstepX(headers)) {
    const trades = parseTopstepX(rows, catalogue, today);
    return { format: 'topstepx', trades, skipped: 0, unrecognised: null };
  }

  if (looksLikeTradesea(headers)) {
    const r = parseTradesea(rows, catalogue, today);
    // ⚠ AN EMPTY RESULT IS A REAL OUTCOME HERE, not a parser failure. The
    // notifications export is a lifecycle log — a session where nothing filled
    // is all `place_order` and `order_working` rows, every one correctly
    // dropped. Saying "no filled rows" beats a silent import of zero trades.
    if (!r.trades.length && !r.skipped) {
      return {
        format: null, trades: [], skipped: 0,
        unrecognised:
          'This looks like a Tradesea export, but it has no filled orders in it — '
          + 'only working, cancelled or non-order events.',
      };
    }
    return withSelfCheck('tradesea', r, rows, catalogue, 0);
  }

  if (looksLikeMotiveWave(headers)) {
    const r = parseMotiveWave(rows, catalogue, today);
    return withSelfCheck('motivewave', r, rows, catalogue, 0);
  }

  // ⚠ TRADOVATE'S PAIRED REPORT IS CHECKED BEFORE SIERRA, and the order matters:
  // a Performance Report carries `Symbol` and a price column, which is most of
  // the Sierra signature. Its buy/sell PAIR of columns is the unambiguous mark,
  // so it is tested first rather than made more specific.
  if (isTradovatePaired(headers)) {
    const p = parseTradovatePaired(rows, catalogue, today);
    return { format: 'tradovate-paired', trades: p.trades, skipped: p.skipped, unrecognised: null };
  }

  if (looksLikeSierra(headers)) {
    const r = parseSierraFills(rows, catalogue, today, prior);
    return withSelfCheck('sierra-fills', r, rows, catalogue, r.carriedIn, {
      carriedIn: r.carriedIn,
      positionMismatches: r.positionMismatches.length,
      unverifiedFills: r.unverifiedFills,
      totalFills: r.totalFills,
      openCloseConflicts: r.openCloseConflicts.length,
      carriedInClosed: r.carriedInClosed ?? [],
    });
  }

  if (classifySheet(headers).includes('atas-fills')) {
    const r = parseAtasFillsFromRows(rows, catalogue, today, prior);
    return withSelfCheck('atas-fills', r, rows, catalogue, 0, {
      carriedInClosed: r.carriedInClosed ?? [],
    });
  }

  // Tradovate ORDER HISTORY (fills). Distinct from the paired report above and
  // routed to the per-fill walker.
  if (looksLikeTradovateFills(headers)) {
    const r = pairFillsIntoTrades(rows, catalogue, today, prior);
    return withSelfCheck('tradovate-fills', r, rows, catalogue, 0, {
      carriedInClosed: r.carriedInClosed ?? [],
    });
  }

  // ⚠ NAMED failure. Running the nearest parser anyway would import trades built
  // from whatever the columns happened to line up with.
  return {
    format: null,
    trades: [],
    skipped: 0,
    unrecognised:
      `These columns don't match a supported export: ${headers.slice(0, 12).join(', ')}` +
      `${headers.length > 12 ? ` (+${headers.length - 12} more)` : ''}. ` +
      `Supported: Sierra Chart fills, ATAS (CSV and XLSX), Tradovate (Performance ` +
      `Report and Order History), Rithmic, TopstepX, Tradesea and MotiveWave.`,
  };
}

/**
 * An ATAS `.xlsx` workbook — EVERY sheet, classified, best one wins.
 *
 * ── 🚨 THE EXECUTIONS SHEET IS PREFERRED OVER PAIRED TRADES ──
 *
 * The old client is explicit about why, and this port had it BACKWARDS with a
 * comment claiming it matched:
 *
 *   "PREFER Executions when both exist — that's the only path that (a) carries
 *    true second-level timestamps … and (b) preserves per-fill detail in
 *    `trade.executions[]`. The paired-trades sheet collapses scaled-out
 *    positions into N separate rows with VWAP'd minute-rounded timestamps —
 *    strictly less information."
 *
 * ⚠ Second-level timestamps and `executions[]` are exactly what PEAK CAPTURE
 * needs (D83). Importing the paired sheet from a workbook that also had fills
 * would have produced trades that can never be peak-reconstructed — silently, and
 * unrecoverably without re-importing.
 *
 * ⚠ It survived because the xlsx path REFUSED EVERY FILE. A dispatch order in
 * code nothing can reach is not ported, it is written down; wiring the unzip up
 * is what exposed it.
 */
export function parseXlsxImport(
  sheets: readonly string[],
  sharedStringsXml: string | null,
  catalogue: readonly ContractSpec[],
  today: string,
): ParseFileResult {
  const strings = sharedStringsXml ? parseXlsxSharedStrings(sharedStringsXml) : [];

  let fills: SheetRow[] | null = null;
  let paired: SheetRow[] | null = null;
  let tt: SheetRow[] | null = null;
  let jigsaw: SheetRow[] | null = null;
  const seenHeaders: string[] = [];

  for (const xml of sheets) {
    const objects = xlsxRowsToObjects(parseXlsxSheet(xml, strings));
    if (!objects.length) continue;

    const headers = Object.keys(objects[0] as Record<string, unknown>);
    const kinds = classifySheet(headers);
    // ⚠ FIRST match of each kind wins, matching the old client's `if (… && !obj)`
    // guards. A workbook can carry two fills sheets and the earlier one is the
    // one the user is looking at.
    if (kinds.includes('atas-fills') && !fills) fills = objects;
    if (kinds.includes('atas-trades') && !paired) paired = objects;
    if (kinds.includes('tt-fills') && !tt) tt = objects;
    if (kinds.includes('jigsaw') && !jigsaw) jigsaw = objects;
    if (!kinds.length && seenHeaders.length < 12) seenHeaders.push(...headers.slice(0, 6));
  }

  // ── 🚨 THE FILLS SHEET IS PREFERRED, BUT NOT WHEN IT IS PROVABLY BROKEN ──
  //
  // The preference is right: per-fill data is the better source whenever it is
  // whole. But "preferred" must not mean "used even when provably incomplete".
  //
  // 🔴 `atasFillsIntegrity` WAS WRITTEN FOR THIS AND WAS NEVER CALLED HERE. The
  // shipping client gates on it; this one exported the function and imported the
  // workbook regardless. Found in the parser sweep on 2026-08-09.
  //
  // What it costs: a real ATAS export wrote five NQU6 sell fills with `Price` =
  // 0. The walker drops any row failing `!qty || !px`, and **the damage is not
  // confined to the dropped rows** — a dropped exit makes a later exit close the
  // wrong episode. On that file the fills path produced **98 trades / $2,674**
  // where the Journal sheet and ATAS's own Statistics sheet both say
  // **164 / $3,277.75**. Sixty-six trades and $604, silently.
  //
  // ⚠ WHOLE-FILE FALLBACK, not per-symbol. Mixing the two sheets gives a total
  // that matches neither — on that file the paths disagree by $1.25 on MES even
  // where the fills ARE complete, because one uses a running VWAP and the other
  // a final one. Falling back wholesale reproduces the vendor's own Statistics
  // total exactly, which is the number the user will check.
  if (fills) {
    const rows = fills.map(adaptXlsxFillRow).filter((x): x is NonNullable<typeof x> => !!x);
    const integrity = atasFillsIntegrity(rows);
    const r = parseAtasFillsFromRows(rows, catalogue, today);
    if (integrity.ok && r.trades.length) {
      return { format: 'atas-fills', trades: r.trades, skipped: r.skipped, unrecognised: null };
    }
    if (!integrity.ok && paired) {
      const trades = parseAtasXlsxRows(paired, catalogue, today);
      if (trades.length) {
        return {
          format: 'atas-xlsx', trades, skipped: 0, unrecognised: null,
          degraded: {
            reason: 'atas-fills-incomplete',
            droppedNoPrice: integrity.droppedNoPrice,
            symbols: integrity.symbols,
            discardedTrades: r.trades.length,
          },
        };
      }
    }
    // No usable Journal sheet to fall back to. Return what the fills gave and
    // let the preview report the damage rather than failing the whole import.
    if (r.trades.length) {
      return {
        format: 'atas-fills', trades: r.trades, skipped: r.skipped, unrecognised: null,
        ...(integrity.ok ? {} : {
          degraded: {
            reason: 'atas-fills-incomplete-no-fallback',
            droppedNoPrice: integrity.droppedNoPrice,
            symbols: integrity.symbols,
            discardedTrades: 0,
          },
        }),
      };
    }
  }

  if (paired) {
    // ⚠ Returns a bare array, unlike the fills parsers' `{ trades, skipped }`.
    // The paired sheet is one row per trade, so there is nothing to skip — a row
    // either parses or the parser drops it, and it reports no count. Stated
    // rather than manufacturing a 0 that implies the parser counted.
    const trades = parseAtasXlsxRows(paired, catalogue, today);
    return { format: 'atas-xlsx', trades, skipped: 0, unrecognised: null };
  }

  // ── PATH 3: a Trading Technologies fills sheet ───────────────────────────
  // ⚠ AFTER the two ATAS paths, matching the old client. A TT sheet cannot
  // satisfy either signature, so the order is convention rather than necessity —
  // kept so the two clients cannot diverge.
  if (tt) {
    const r = parseTtFills(tt, catalogue, today);
    // ⚠ Every row synthetic should be impossible — a synthetic order only fills
    // by way of a child order at the exchange. Falling through to "no supported
    // sheet" would blame the file for something the filter did.
    if (r.onlySynthetic) {
      return {
        format: null, trades: [], skipped: 0,
        unrecognised:
          `This Trading Technologies export contains only synthetic-engine rows `
          + `(${r.synthetic} routed "…SE") and none of the exchange fills they produced. `
          + `Importing them would count each fill several times, so nothing was read. `
          + `Re-export with the exchange fills included.`,
      };
    }
    if (r.synthetic) {
      // ⚠ Told, not swallowed. On the real export this is 4 rows out of 71, and
      // those 4 are the difference between ending flat and holding a phantom
      // 20-lot short.
      console.info(
        `Trading Technologies import: ignored ${r.synthetic} synthetic-engine row(s). `
        + `TT lists a synthetic order's fill on every leg of the chain alongside the `
        + `exchange fill that actually happened; importing them counts the same fill `
        + `two or three times.`,
      );
    }
    if (r.trades.length) {
      return { format: 'tt-xlsx-fills', trades: r.trades, skipped: r.skipped, unrecognised: null };
    }
  }

  // ── PATH 4: a Jigsaw "MyTrades" report ───────────────────────────────────
  // ⚠ LAST, matching the old client. Its signature (AvgPrice + NetPnL + Symbol)
  // is the loosest of the four, so anything that could also be an ATAS or TT
  // sheet has already claimed the file.
  if (jigsaw) {
    const trades = parseJigsawRows(jigsaw, catalogue, today);
    // ⚠ One row per trade, so there is nothing to "skip" — a row either parses
    // or the parser drops it, and it reports no count. Stated rather than
    // manufacturing a 0 that implies the parser counted.
    if (trades.length) {
      return { format: 'jigsaw-xlsx', trades, skipped: 0, unrecognised: null };
    }
  }

  return {
    format: null,
    trades: [],
    skipped: 0,
    unrecognised:
      `None of the ${sheets.length} sheet${sheets.length === 1 ? '' : 's'} in that workbook `
      + `match a supported export. Columns found: `
      + `${[...new Set(seenHeaders)].slice(0, 12).join(', ') || '(all sheets empty)'}. `
      + `Workbooks are read as ATAS (Executions or Trades), Trading Technologies fills, `
      + `or a Jigsaw "MyTrades" report.`,
  };
}
